import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { TranscriptStreamService } from './transcript-stream.service';
import { TranscriptWsEnvelope } from './transcript.types';

interface OwnerConnection {
  socket$: WebSocketSubject<TranscriptWsEnvelope>;
  events$: Subject<TranscriptWsEnvelope>;
}

/**
 * Opens one `client_type=app` WebSocket per device the logged-in owner is
 * watching (typically 1, plus an autocaption companion), and registers each
 * stream with TranscriptStreamService. The owner main app has no transcript WS
 * of its own otherwise — guest/av_view reuse their existing connections.
 *
 * Reconnect rules mirror the other WS services: no reconnect on close codes
 * 1000 (normal) / 1008 (policy/JWT) or after an explicit disconnect.
 */
@Injectable({ providedIn: 'root' })
export class OwnerTranscriptConnectionManager {
  private readonly server = environment.speaksee.websocketUrl;
  private readonly connections = new Map<string, OwnerConnection>();
  private readonly manualClose = new Set<string>();

  /** Overridable for tests. */
  private wsFactory = webSocket;

  constructor(private transcriptStream: TranscriptStreamService) {}

  /** Open + register a connection for a device (idempotent per device). */
  connect(deviceId: string, jwt: string): void {
    if (!deviceId || !jwt || this.connections.has(deviceId)) return;
    this.manualClose.delete(deviceId);

    const events$ = new Subject<TranscriptWsEnvelope>();
    const params = new URLSearchParams({ client_type: 'app', uuid: deviceId, jwt });
    const socket$ = this.wsFactory<TranscriptWsEnvelope>({
      url: `${this.server}?${params.toString()}`,
      closeObserver: {
        next: (event: CloseEvent) => this.onClose(deviceId, jwt, event),
      },
    });

    this.connections.set(deviceId, { socket$, events$ });
    this.transcriptStream.register(deviceId, events$);

    socket$.subscribe({
      next: (msg) => events$.next(msg),
      error: () => this.onError(deviceId),
    });
  }

  /** Tear down a single device connection. */
  disconnect(deviceId: string): void {
    const conn = this.connections.get(deviceId);
    if (!conn) return;
    this.manualClose.add(deviceId);
    conn.socket$.complete();
    conn.events$.complete();
    this.connections.delete(deviceId);
    this.transcriptStream.unregister(deviceId);
  }

  /** Tear down every owner connection (call on leaving the speech page). */
  disconnectAll(): void {
    for (const deviceId of Array.from(this.connections.keys())) {
      this.disconnect(deviceId);
    }
  }

  private onClose(deviceId: string, jwt: string, event: CloseEvent): void {
    this.transcriptStream.unregister(deviceId);
    this.connections.delete(deviceId);
    const noReconnect =
      event.code === 1000 || event.code === 1008 || this.manualClose.has(deviceId);
    // TODO(transcripts-over-ws follow-up): reconnect is immediate with no backoff
    // or attempt cap (see plan Task 7 note), and reuses the original jwt which may
    // have expired (a 1008 will then stop reconnect, silently dropping the stream).
    // Add exponential backoff + JWT refresh from AccountService here.
    if (!noReconnect) {
      this.connect(deviceId, jwt);
    }
  }

  private onError(deviceId: string): void {
    // Cleanup only. The browser fires `onclose` immediately after `onerror`, so
    // `onClose` is the single authoritative reconnect path — reconnecting here too
    // would open a duplicate connection on every unclean close.
    const conn = this.connections.get(deviceId);
    conn?.events$.complete();
    this.connections.delete(deviceId);
    this.transcriptStream.unregister(deviceId);
  }
}
